import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useZugang } from '@/hooks/useZugang';
import type { ZugangBereich, ZugangFachbereich } from '@/types';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
  /**
   * Bereich aus der Berechtigungsliste. Prüft ZUSÄTZLICH zur Rolle — die Rolle
   * sagt, wofür jemand zuständig ist, die Liste sagt, was die Geschäftsführung
   * ausdrücklich freigegeben hat.
   */
  bereich?: ZugangBereich;
  /** Zugang, wenn MINDESTENS einer dieser Bereiche freigeschaltet ist. */
  bereichAny?: ZugangBereich[];
  /**
   * Zugang nur für diese Fachbereiche. Anders als `bereich` NICHT fail-open:
   * solange der Fachbereich nicht geladen ist, wird umgeleitet. Das Backend
   * (PC-31) antwortet ohnehin mit 403 — hier nur, damit niemand auf einer
   * Seite landet, die dann leer bleibt.
   */
  nurFachbereich?: ZugangFachbereich[];
}

export function ProtectedRoute({ children, allowedRoles, bereich, bereichAny, nurFachbereich }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { darf, geladen, laedt, fachbereich } = useZugang();
  /** Nur Routen mit Zugangsbedingung muessen auf die Rechte warten. */
  const brauchtRechte = !!(bereich || bereichAny || nurFachbereich);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Multi-Role-Support: user.role kann "office,techniker" sein (split bei Komma/Pipe/Whitespace).
  // Case-insensitive matching. Siehe Home.tsx / api.ts userHasRole() für gleiche Logik.
  if (allowedRoles && user) {
    const userRoleList = (user.role || '')
      .toLowerCase()
      .split(/[,|\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);
    const allowedLower = allowedRoles.map((r) => r.toLowerCase());
    const hasAccess = allowedLower.some((a) => userRoleList.includes(a));
    if (!hasAccess) {
      return <Navigate to="/leckortung/new" replace />;
    }
  }

  // Wer eine geschützte Seite direkt aufruft, soll weder weggeworfen werden
  // noch kurz Inhalte sehen, die ihm nicht zustehen: solange der Kaltstart
  // läuft, wird gewartet statt geraten. Ein Fehlschlag ist etwas anderes —
  // dann greift unten wieder Fail-open, damit kein Netzausfall jemanden aus
  // seiner eigenen App aussperrt. Die Prüfung, die zählt, sitzt im Backend.
  if (brauchtRechte && laedt) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Ist nichts geladen (Fehlschlag), sperrt `darf` nicht.
  if (bereich && geladen && !darf(bereich)) {
    return <Navigate to="/home" replace />;
  }
  if (bereichAny && geladen && !bereichAny.some((b) => darf(b))) {
    return <Navigate to="/home" replace />;
  }
  if (nurFachbereich && geladen && !nurFachbereich.includes(fachbereich as never)) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}
